import { appendResponseHeader } from 'h3';
import type {JobDetails, JobDetailsRedirect} from "~/types/job";

export default defineNuxtRouteMiddleware(async (to) => {
  // Skip middleware on client hydration
  if (import.meta.client && useNuxtApp().isHydrating) {
    return;
  }
  if (useError().value) {
    return;
  }

  const { public: { wadApiBaseUrl } } = useRuntimeConfig();
  const localePath = useLocalePath();
  const { getToken } = useAuthUser();
  const path = to.path;

  // Determine job type
  let jobType: 'internal' | 'external' | 'preview' | null = null;
  switch (true) {
    case /^\/companies\/[^/]+\/[^/]+\/[^/]+\/[^/]+$/.test(path):
      jobType = 'internal';
      break;
    case /^\/jobs\/ext\/[^/]+\/[^/]+$/.test(path):
      jobType = 'external';
      break;
    case /^\/jobs\/[^/]+\/preview$/.test(path):
      jobType = 'preview';
      break;
    default:
      return abortNavigation(createError({
        statusCode: 404,
        message: 'Job not found',
        fatal: true,
      }));
  }

  let apiUrl = `/v2/jobs/details?job_slug=${to.params.slug}&job_id=${to.params.id}`;
  switch (jobType) {
    case 'internal':
      apiUrl += `&company_id=${to.params.companyId}&company_slug=${to.params.companySlug}`;
      break;
    case 'external':
      apiUrl += '&external=true';
      break;
    case 'preview':
      apiUrl = `/v2/jobs/${to.params.id}/draft-preview`;
      break;
  }

  const event = import.meta.server ? useRequestEvent() : null;
  const token = await getToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {} as HeadersInit;

  let responseData: JobDetails | null = null;
  let redirectUrl: string | null = null;

  try {
    responseData = await $fetch<JobDetails>(apiUrl, {
      baseURL: wadApiBaseUrl,
      credentials: 'omit',
      redirect: 'manual',
      headers,
      onResponse({ response }) {
        if (response.status === 301 && response._data) {
          const data = response._data as JobDetailsRedirect;
          switch (jobType) {
            case 'internal':
            case 'preview':
              redirectUrl = localePath({
                name: 'job-details-internal',
                params: {
                  id: data.job_id,
                  slug: data.job_slug,
                  companyId: data.company_id,
                  companySlug: data.company_slug,
                },
              });
              break;
            case 'external':
              redirectUrl = localePath({
                name: 'job-details-external',
                params: {
                  id: data.job_id,
                  slug: data.job_slug,
                },
              });
              break;
          }
        }
      },
    });

    // Immediately navigate if redirect URL is different from current path
    if (redirectUrl) {
      return navigateTo(redirectUrl, { redirectCode: 301 });
    }

  } catch (err: any) {
    const statusCode = err?.status ?? err?.statusCode ?? 500;

    // ONLY cache 404s
    if (statusCode === 404 && jobType === 'internal' && event) {
      appendResponseHeader(
          event,
          'Cache-Tag',
          `job-internal-${to.params.companyId}-${to.params.id}`
      );
    }

    return abortNavigation(createError({
      statusCode,
      fatal: true,
    }));
  }


  // Save job details for downstream use
  if (responseData) {
    const jobDetails = useState<JobDetails | null>('jobDetails', () => null);
    jobDetails.value = responseData;
  }
});
